import React from 'react';

import { targetingInactiveAudienceSegments } from '~/src/dash/api/audience-segment';
import { getAudienceSegments, IAudienceSegment } from '~/src/dash/api/console-api';
import {
  EAllowStatus,
  IChannelGroup,
  IHeaderBidder,
  ISite,
  ResourceActiveEnabled,
  SegmentTargetingOR,
} from '~/src/dash/api/console-api';
import {
  createDeal,
  DealType,
  deleteDeal,
  getDeal,
  getIndexExchangeSiteIDs,
  IDeal,
  IIndexExchangeToPublicaApiDeal,
  ISiteAndInventoryGroup,
  IUnrulyDealParams,
  updateDeal,
} from '~/src/dash/api/deals';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  IInventoryRouteParams,
  navigate,
  parseLocation,
  RouteDealEdit,
  RouteDealList,
  RouteDealNew,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { getSessionPublisherId } from '~/src/dash/utils/session';
import { DealSettingsForm } from '~/src/dash/views/deal/deal-settings-form';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    IUserStateProps,
    ISessionStateProps,
    IHistoryProps {}

interface IState {
  snackbarMessage: string;
  snackbarError: boolean;
  loading: boolean;
  loadingIndexExchangeData: boolean;
  tokenStatus: boolean; // false means it expired
  deal: IDeal;
  audienceSegments: IAudienceSegment[];
  siteIDsList: IMenuList;
  inventoryGroupIDsList: IMenuList;
}

export const DealSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        session: state.session,
      };
    },
  },
  class extends React.Component<IProps, IState> {
    constructor(props: IProps) {
      super(props);
      const Name = 'deal-' + new Date().getTime();
      const publisherId = getSessionPublisherId(this.props.session);

      this.state = {
        snackbarMessage: '',
        snackbarError: false,
        loading: true,
        loadingIndexExchangeData: true,
        tokenStatus: false,

        deal: {
          // Gorm fields.
          ID: 0,

          // Object custom fields.
          Active: ResourceActiveEnabled,
          Name,
          PublisherID: publisherId,
          BidderType: '', // SSP Type
          DealKey: '',
          SspParams: {
            Buyers: [
              {
                Adomain: '',
                SeatID: '',
                Active: true,
              },
            ],
          } as IUnrulyDealParams,
          DealType: DealType.OpenAuction,
          StartDate: '',
          EndDate: '',
          ImpressionGoal: '0',
          // Targeting
          AudienceTargeting: '',
          AudienceTargetingRelationshipType: EAllowStatus.ALLOW,
          AudienceTargetingOperationType: SegmentTargetingOR,
          HeaderBidders: [] as IHeaderBidder[],
          Channels: [] as ISite[],
          ChannelGroups: [] as IChannelGroup[],
        } as IDeal,
        audienceSegments: [] as IAudienceSegment[],
        siteIDsList: {} as IMenuList,
        inventoryGroupIDsList: {} as IMenuList,
      };
    }

    componentDidMount() {
      this.loadAudienceSegments();
      this.loadDeal();
      this.loadIndexExchangeSiteIDs();
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
      // if this is the first load, get the deal
      // if we transition between new and clone routes, refresh the deal
      if (
        !prevProps ||
        prevProps.location.pathname + prevProps.location.search !==
          this.props.location.pathname + this.props.location.search
      ) {
        this.loadAudienceSegments();
        this.loadDeal();
      }
    }

    loadDeal = () => {
      // based on the url path, this could be a new, edit or clone attempt
      const loc = parseLocation(this.props.location.pathname, this.props.location.search);

      const dealId = loc.deal || loc.cloneId;
      const isClone = dealId === loc.cloneId;

      // we are provided a DealId
      if (dealId) {
        getDeal(dealId, this.props.session.publisher.ID)
          .then((deal: IDeal) => {
            // ---- Deal get OK.
            let normalizedDeal = deal;
            if (isClone) {
              normalizedDeal = cleanDealForCloning(normalizedDeal);
            }

            this.setState({
              deal: normalizedDeal,
              loading: false,
            });
          })
          .catch((reason: any) => {
            // ---- There was an error accessing the deal.

            // Create the error message.
            let errorMsg: string = '';
            if (reason.message) {
              // Error message in the response: use it.
              errorMsg = `Error while loading the deal: ${reason.message}`;
              // If the API couldn't enrich the deal list, it means there was some kind of authentication error.
              if (reason.message.includes("can't enrich the deal list")) {
                errorMsg +=
                  '\nPlease check your account credentials in the "Manage third-party accounts" panel.';
              }
            } else {
              // No error message in the response: create generic error.
              errorMsg = `Error while loading the deal.`;
            }

            // Display snackbar and return to previous page after 5 secs.
            this.setState(
              {
                snackbarMessage: errorMsg,
                snackbarError: true,
              },
              () => {
                setTimeout(() => {
                  navigate(
                    RouteDealList,
                    extractRouteParams({
                      session: this.props.session,
                      user: this.props.user,
                    }),
                  );
                }, 5000);
              },
            );
          });
      } else {
        // This is a new deal.
        this.setState({
          loading: false,
        });
      }
    };

    loadAudienceSegments = () => {
      getAudienceSegments({
        query: {
          publisher_id: this.props.session.publisher.ID,
        },
      })
        .then((audienceSegments: IAudienceSegment[]) => {
          this.setState({
            audienceSegments,
          });
        })
        .catch(() => {
          this.setState({
            snackbarMessage: 'Error while getting audience segments.',
            snackbarError: true,
          });
        });
    };

    loadIndexExchangeSiteIDs = () => {
      getIndexExchangeSiteIDs(this.props.session.publisher.ID)
        .then((res: [ISiteAndInventoryGroup | undefined, string | undefined]) => {
          if (res[0] !== undefined) {
            const siteAndInventoryGroupIDs = res[0];
            const siteIDsList: IMenuList = {};
            const inventoryGroupIDsList: IMenuList = {};
            for (const site of siteAndInventoryGroupIDs.sites || []) {
              siteIDsList[site.id] = {
                title: site.name,
                selected: (
                  (this.state.deal.SspParams as IIndexExchangeToPublicaApiDeal).targetingSiteIDs ||
                  []
                ).includes(site.id),
                ID: site.id.toString(),
              };
            }
            for (const inventoryGroup of siteAndInventoryGroupIDs.inventory_groups || []) {
              inventoryGroupIDsList[inventoryGroup.id] = {
                title: inventoryGroup.name,
                selected: (
                  (this.state.deal.SspParams as IIndexExchangeToPublicaApiDeal)
                    .targetingInventoryGroupIDs || []
                ).includes(inventoryGroup.id),
                ID: inventoryGroup.id.toString(),
              };
            }
            this.setState({
              siteIDsList,
              inventoryGroupIDsList,
              loadingIndexExchangeData: false,
              tokenStatus: true,
            });
          } else {
            this.setState({
              loadingIndexExchangeData: false,
              tokenStatus: false,
            });
          }
        })
        .catch(() => {
          this.setState({
            snackbarMessage: 'Error while getting site ids.',
            snackbarError: true,
            loadingIndexExchangeData: false,
            tokenStatus: false,
          });
        });
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleClose = () => {
      navigate(
        RouteDealList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // the form will emit a new deal object on change
    handleChange = (deal: IDeal) => {
      this.setState({
        deal,
      });
    };

    handleSubmit = (deal: IDeal): void => {
      // const validationErrors = this.validateNetwork(); // TODO
      const validationErrors = [] as string[]; // TODO

      // targetingInactiveAudienceSegments
      if (targetingInactiveAudienceSegments(this.state.audienceSegments, deal.AudienceTargeting)) {
        validationErrors.push(
          'Remove inactive audience segments or activate the inactivate audience segments.',
        );
      }

      if (validationErrors.length > 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: validationErrors.join(', '),
        });
        return;
      }

      if (deal.ID === 0) {
        createDeal(deal, this.props.session.publisher.ID)
          .then((deal: IDeal) => {
            // ---- Create deal OK
            // Open the deal in "edit" mode.
            navigate(RouteDealEdit, extractRouteParams(this.props), {
              dealID: deal.ID,
            });
          })
          .catch((reason: any) => {
            // ---- An error happened. Display an error message.

            // Create the error message.
            let errorMsg: string = '';
            if (reason.message) {
              // Error message in the response: use it.
              errorMsg = `Error while loading the deal: ${reason.message}`;
              // If the API couldn't enrich the deal list, it means there was some kind of authentication error.
              if (reason.message.includes("can't enrich the deal list")) {
                errorMsg +=
                  '\nPlease check your account credentials in the "Manage third-party accounts" panel.';
              }
            } else {
              // No error message in the response: create generic error.
              errorMsg = `Error while loading the deal.`;
            }

            // Display snackbar.
            this.setState({
              snackbarMessage: errorMsg,
              snackbarError: true,
            });
          });
      } else {
        // updateDeal is called with fullUpdate true since we are passing in the entire network
        // object including the children (NetworkTrackers, NetworkPublisherMaps and Channels)
        // all fields must be present since this will not selectively update fields
        updateDeal(deal, this.props.session.publisher.ID, true)
          .then(() => {
            navigate(RouteDealList, extractRouteParams(this.props));
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'Failed to save deal.',
              snackbarError: true,
            });
          });
      }
    };

    handleDelete = (deal?: IDeal) => {
      // if this is a new network or cloned network, navigate to list
      if (!deal || deal.ID === 0) {
        this.handleClose();
      } else {
        deleteDeal(deal.ID, this.props.session.publisher.ID)
          .then(() => {
            this.handleClose();
          })
          .catch((err: any) => {
            console.error(err);

            this.setState({
              snackbarMessage: 'Error while deleting deal.',
              snackbarError: true,
            });
          });
      }
    };

    render() {
      const {
        audienceSegments,
        deal,
        siteIDsList,
        inventoryGroupIDsList,
        snackbarMessage,
        snackbarError,
        loading,
        loadingIndexExchangeData,
        tokenStatus,
      } = this.state;
      const publisherId = this.props.session.publisher.ID;

      return (
        <>
          {loading || loadingIndexExchangeData ? (
            <LoadingView active={true} text='Loading the deal settings' />
          ) : (
            <DealSettingsForm
              deal={deal}
              audienceSegments={audienceSegments}
              siteIDsList={siteIDsList}
              inventoryGroupIDsList={inventoryGroupIDsList}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              onClose={this.handleClose}
              onDelete={this.handleDelete}
              cloneUrl={buildDealCloneUrl(deal, publisherId)}
              tokenStatus={tokenStatus}
            />
          )}

          {/* SNACKBAR (for notifications) */}
          <SnackbarView
            open={!!snackbarMessage}
            error={!!snackbarError}
            onClose={this.snackbarClose}
          >
            {this.state.snackbarMessage}
          </SnackbarView>
        </>
      );
    }
  },
);

function cleanDealForCloning(deal: IDeal): IDeal {
  return {
    ...deal,
    ID: 0,
    Name: `${deal.Name} - clone`,
    CreatedAt: '',
    UpdatedAt: '',
    DeletedAt: '',

    DealKey: '',

    SspParams: {
      ...deal.SspParams,
      RhythmoneDealID: 0,
    },
  };
}

// keeping this here for now until there is a need to create a generic util for clone routes
export function buildDealCloneUrl(deal: IDeal | undefined, publisherID: string): string {
  const baseNewUrl = getRoutePath(RouteDealNew, { pub: publisherID } as IInventoryRouteParams, {
    dealID: deal && deal.ID,
  });

  if (!deal || !deal.ID) {
    return baseNewUrl;
  }

  return baseNewUrl + `?cloneId=${deal.ID}`;
}
